import { useState } from "react"
import { useSelector } from "react-redux"
import { selectTranslations } from "../../services/i18n/i18nSlice"
import { TitleH1 } from "../../components/TitleH1"
import { Text } from "../../components/Text"
import { Button } from "../../components/Button"
import Anchor from "../../components/Anchor"
import BRAND, { useLogo } from "../../brand";
import { TENANT } from "../../config/constants"
import { GetFromLocalStorage, SaveToLocalStorage } from "../../utils/utils"

// lucide (ISC) paths, inline so they take the color of the text
const ICONS = [
    <><rect width="8" height="4" x="8" y="2" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M12 11h4" /><path d="M12 16h4" /><path d="M8 11h.01" /><path d="M8 16h.01" /></>,
    <><path d="M12 7v14" /><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" /></>,
    <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
    <><path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" /><circle cx="12" cy="8" r="6" /></>,
]

const FeatureIcon = ({index, size}) =>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {ICONS[index]}
    </svg>

const INTRO_SEEN_KEY = `${TENANT}_login_intro_seen`

// brands with loginIntro: the micro onboarding is shown once, on phones only: tablets and desktops get the side panel
export const shouldShowLoginIntro = () =>
    BRAND.loginIntro && window.matchMedia('(max-width: 767.98px)').matches && !GetFromLocalStorage(INTRO_SEEN_KEY)

// desktop: explanation panel next to the login
export function LoginAside() {
    const t = useSelector(selectTranslations)
    return (
        <aside className="login_aside">
            <div className="login_aside_content">
                <h2>{t.login.aside_title}</h2>
                <p className="login_aside_text">{t.login.aside_text}</p>
                <ul>
                    {t.login.features.map((f, i) =>
                        <li key={i}>
                            <FeatureIcon index={i} size={28} />
                            <div>
                                <h3>{f.title}</h3>
                                <p>{f.text}</p>
                            </div>
                        </li>
                    )}
                </ul>
            </div>
        </aside>
    )
}

// mobile: one feature per step, then the login
export function LoginOnboarding({onDone}) {
    const logo = useLogo()
    const t = useSelector(selectTranslations)
    const [step, setStep] = useState(0)
    const features = t.login.features
    const isLast = step === features.length - 1

    const done = () => {
        SaveToLocalStorage(INTRO_SEEN_KEY, true)
        onDone()
    }

    return (
        <div className="login_onboarding fh-div d-flex flex-column justify-content-between">
            <div className="d-flex justify-content-between align-items-center">
                <img src={logo} className="login_onboarding_logo" alt={BRAND.name} />
                {!isLast && <Anchor href="#" fontSize="14px" onClick={(e)=>{e.preventDefault(); done()}}>{t.login.intro_skip}</Anchor>}
            </div>
            <div>
                <div className="login_onboarding_icon"><FeatureIcon index={step} size={40} /></div>
                <TitleH1 className="login_title">{features[step].title}</TitleH1>
                <Text>{features[step].text}</Text>
            </div>
            <div>
                <div className="login_onboarding_dots">
                    {features.map((_, i) => <span key={i} className={i === step ? 'active' : ''} />)}
                </div>
                <Button className="w-100 mx-auto" onClick={isLast ? done : () => setStep(step + 1)}>
                    {isLast ? t.login.intro_start : t.login.intro_next}
                </Button>
            </div>
        </div>
    )
}
